/**
 * ExternalStampSection
 *
 * Self-contained "Upload Stamped PDF" flow rendered in DocumentAnnotator's
 * toolbar area. Gated by:
 *   1. Master feature flag: document_review
 *   2. Sub-flag: document_review_external_stamp
 *   3. Internal-actor check (role !== EXTERNAL)
 *   4. Review in active or pending_external_confirmation state
 *
 * State machine: idle → uploading → pending → confirmed (locked)
 *                                          ↘ idle (re-upload)
 */
import { useState, useRef, useCallback } from 'react';
import { Upload, CheckCircle, RotateCcw, Copy, Loader2, AlertCircle, FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import type { UploadHTTPError } from '@/lib/api';
import { useAuth } from '@/lib/auth/AuthProvider';
import { useFeature, useFeaturesLoading } from '@/lib/features/FeatureProvider';
import { UserRole } from '@/types/auth';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Signer {
  subject_dn: string;
  issuer_dn: string;
  claimed_signing_time: string;
  not_before: string;
  not_after: string;
}

interface InspectorResult {
  is_signed: boolean;
  signers: Signer[];
  known_ca_match: string | null;
  // Backend may not send issuer_dn_short; fall back to first signer's issuer_dn.
  issuer_dn_short?: string | null;
  pdf_sha256: string;
  differs_from_reference: boolean;
}

type UploadState = 'idle' | 'uploading' | 'pending' | 'confirmed';

interface ExternalStampSectionProps {
  documentReviewId: string;
  reviewStatus?: string;
  /** When the review is already locked + externally-stamped, these surface the
   *  cyan badge + audit cert button on re-open (not just during the confirm
   *  session). Set by the parent from the loaded review record. */
  externallyStampedPdfRef?: string | null;
  auditCertPdfRef?: string | null;
  onConfirmed?: () => void;
}

// ---------------------------------------------------------------------------
// Helpers (module-level, outside component)
// ---------------------------------------------------------------------------

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB

function validateFile(file: File): string | null {
  if (file.size > MAX_FILE_SIZE_BYTES) return 'File exceeds 50MB';
  if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
    return 'File must be a PDF';
  }
  return null;
}

function truncateSha256(sha: string): string {
  return sha.slice(0, 8);
}

/**
 * Iter 3 B7: strip non-production qualifier parentheticals from cert DN
 * strings on the customer-facing pending-confirmation panel. Mirrors the
 * audit_cert_generator._sanitize_dn() Python helper — the underlying
 * cryptographic CN is left alone in storage, this is display-only.
 */
function sanitizeDn(dn: string | null | undefined): string {
  if (!dn) return '';
  return dn
    .replace(/\s*\(\s*(demo[^)]*|test[^)]*|self[\s-]?signed[^)]*|sample[^)]*)\)/gi, '')
    .trim();
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ExternalStampSection({
  documentReviewId,
  reviewStatus,
  externallyStampedPdfRef,
  auditCertPdfRef,
  onConfirmed,
}: ExternalStampSectionProps) {
  const { user } = useAuth();
  const featuresLoading = useFeaturesLoading();
  const masterFlag = useFeature('document_review');
  const subFlag = useFeature('document_review_external_stamp');
  const queryClient = useQueryClient();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [inspectorResult, setInspectorResult] = useState<InspectorResult | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [auditCertRef, setAuditCertRef] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Gating — render nothing until flags loaded, or if conditions not met
  // ---------------------------------------------------------------------------

  if (featuresLoading) return null;
  if (!masterFlag || !subFlag) return null;
  if (user?.role === UserRole.EXTERNAL) return null;

  // Locked + externally-stamped reviews show the persistent confirmed banner
  // (cyan badge + audit cert button) on re-open, even though the upload flow
  // itself only allows active / pending_external_confirmation transitions.
  const isLockedExternallyStamped =
    reviewStatus === 'locked' && !!externallyStampedPdfRef;

  if (
    reviewStatus !== 'active' &&
    reviewStatus !== 'pending_external_confirmation' &&
    !isLockedExternallyStamped
  ) {
    return null;
  }

  // ---------------------------------------------------------------------------
  // Upload handler
  // ---------------------------------------------------------------------------

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input so the same file can be re-selected on re-upload
    e.target.value = '';

    const validationError = validateFile(file);
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }

    setErrorMsg(null);
    setUploadState('uploading');
    setUploadProgress(0);

    try {
      const result = await api.documentReviews.uploadExternallyStamped(
        documentReviewId,
        file,
        (pct) => setUploadProgress(pct)
      );

      setInspectorResult(result.inspector_result);
      setUploadState('pending');
    } catch (err: unknown) {
      // HTTP 400 = backend parse/inspect failure → spec-exact corrupted PDF message (AC6d)
      const httpStatus = (err as UploadHTTPError).httpStatus;
      const message = err instanceof Error ? err.message : 'Upload failed';
      const displayMsg = httpStatus === 400
        ? 'PDF could not be parsed; the file may be damaged'
        : (message || 'Upload failed');
      setErrorMsg(displayMsg);
      setUploadState('idle');
    }
  }, [documentReviewId]);

  // ---------------------------------------------------------------------------
  // Confirm + Lock handler
  // ---------------------------------------------------------------------------

  const handleConfirm = useCallback(async () => {
    setConfirming(true);
    try {
      const result = await api.documentReviews.confirmExternalStamp(documentReviewId);
      if (result.audit_cert_pdf_ref) setAuditCertRef(result.audit_cert_pdf_ref);
      setUploadState('confirmed');
      queryClient.invalidateQueries({ queryKey: ['document-review', documentReviewId] });
      toast.success('Review locked as Externally Stamped');
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Confirm failed');
    } finally {
      setConfirming(false);
    }
  }, [documentReviewId, queryClient, onConfirmed]);

  // ---------------------------------------------------------------------------
  // Copy SHA256
  // ---------------------------------------------------------------------------

  const handleCopySha = useCallback(() => {
    if (!inspectorResult) return;
    navigator.clipboard.writeText(inspectorResult.pdf_sha256).then(() => {
      toast.success('SHA256 copied');
    }).catch(() => {
      toast.error('Copy failed');
    });
  }, [inspectorResult]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // Confirmed state — show badge + audit cert link (locked view).
  // Either: the user just clicked Confirm in this session (uploadState),
  // OR the review was already locked + externally-stamped on load (re-open path).
  const persistentAuditCertRef = auditCertRef || auditCertPdfRef;
  if (uploadState === 'confirmed' || isLockedExternallyStamped) {
    return (
      <div
        className="flex items-center gap-2 px-4 py-2 border-b bg-cyan-50"
        data-testid="external-stamp-confirmed"
      >
        <CheckCircle className="w-4 h-4 text-cyan-700" />
        <Badge variant="external-stamp">Externally Stamped</Badge>
        <span className="text-sm text-muted-foreground">Review locked</span>
        {persistentAuditCertRef && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-cyan-700 hover:text-cyan-900 ml-auto"
            onClick={() => api.documentReviews.downloadAuditCert(documentReviewId)}
            data-testid="audit-cert-download-link"
          >
            <FileDown className="w-3.5 h-3.5 mr-1" />
            Audit Certificate
          </Button>
        )}
      </div>
    );
  }

  // Uploading state
  if (uploadState === 'uploading') {
    return (
      <div
        className="flex items-center gap-3 px-4 py-2 border-b bg-background"
        data-testid="external-stamp-uploading"
      >
        <Loader2 className="w-4 h-4 animate-spin text-cyan-700" />
        <div className="flex-1">
          <div className="text-sm text-muted-foreground mb-1">Uploading stamped PDF…</div>
          <div className="w-full bg-slate-200 rounded-full h-1.5">
            <div
              className="bg-cyan-600 h-1.5 rounded-full transition-all"
              style={{ width: `${uploadProgress}%` }}
              data-testid="upload-progress-bar"
            />
          </div>
        </div>
        <span className="text-xs text-muted-foreground w-10 text-right">{uploadProgress}%</span>
      </div>
    );
  }

  // Pending confirmation state — inspector results panel
  if (uploadState === 'pending' && inspectorResult) {
    const signatureAbsent = !inspectorResult.is_signed;
    const sha8 = truncateSha256(inspectorResult.pdf_sha256);
    const firstSigner = inspectorResult.signers[0];
    const subjectClean = sanitizeDn(firstSigner?.subject_dn);
    const issuerClean = sanitizeDn(
      inspectorResult.issuer_dn_short || firstSigner?.issuer_dn || ''
    );
    // Iter3 B7/V24: when subject==issuer the cert is self-signed; suppress the
    // "Other issuer:" row that would otherwise duplicate the signer line and
    // read as data noise. Mirrors audit_cert_generator's "Self-signed
    // certificate" badge.
    const isSelfSigned =
      !!firstSigner &&
      sanitizeDn(firstSigner.subject_dn) === sanitizeDn(firstSigner.issuer_dn);

    return (
      <div
        className="border-b bg-slate-50 px-4 py-3 space-y-3"
        data-testid="external-stamp-pending"
      >
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-800">Pending Confirmation</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => {
              setUploadState('idle');
              setInspectorResult(null);
              setErrorMsg(null);
              // Defer click until after React re-renders and mounts the file input.
              setTimeout(() => fileInputRef.current?.click(), 0);
            }}
            data-testid="reupload-button"
          >
            <RotateCcw className="w-3 h-3 mr-1" />
            Re-upload
          </Button>
        </div>

        {/* Signature absent warning */}
        {signatureAbsent && (
          <div
            className="flex items-start gap-2 p-2 rounded bg-amber-50 border border-amber-200 text-amber-800 text-sm"
            data-testid="no-signature-warning"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>No signature detected on this PDF. Re-stamp with your CertifiO tool and try again.</span>
          </div>
        )}

        {/* Signers list */}
        {inspectorResult.signers.length > 0 && (
          <div className="space-y-1.5" data-testid="signers-list">
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Signers</div>
            {inspectorResult.signers.map((signer, i) => (
              <div key={i} className="text-sm space-y-0.5 pl-2 border-l-2 border-cyan-300">
                <div className="font-medium text-slate-800" data-testid={`signer-name-${i}`}>
                  {sanitizeDn(signer.subject_dn)}
                </div>
                <div className="text-xs text-muted-foreground">
                  Signed: {formatDateTime(signer.claimed_signing_time)}
                </div>
                <div className="text-xs text-muted-foreground">
                  Cert valid: {formatDateTime(signer.not_before)} – {formatDateTime(signer.not_after)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CA badge */}
        <div data-testid="ca-badge">
          {inspectorResult.known_ca_match ? (
            <Badge variant="secondary" className="text-xs">
              Recognized professional CA: {inspectorResult.known_ca_match}
            </Badge>
          ) : isSelfSigned ? (
            <Badge variant="outline" className="text-xs">
              Self-signed certificate
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs">
              Other issuer: {issuerClean}
            </Badge>
          )}
        </div>

        {/* SHA256 */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="sha256-row">
          <span className="font-mono">{sha8}…</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-5 w-5 p-0"
            onClick={handleCopySha}
            title="Copy full SHA256"
            data-testid="copy-sha-button"
          >
            <Copy className="w-3 h-3" />
          </Button>
        </div>

        {/* Differs from reference. Iter3 V25: when an embedded signature is
         *  detected, the "Identical to original" warning is misleading (the
         *  bytes carry a PAdES signature even if the diff heuristic missed it).
         *  Suppress in that case so the panel doesn't contradict itself. */}
        {(inspectorResult.differs_from_reference || !signatureAbsent) && (
          <div
            className={`text-xs ${inspectorResult.differs_from_reference ? 'text-cyan-700' : 'text-slate-500'}`}
            data-testid="differs-from-reference"
          >
            {inspectorResult.differs_from_reference
              ? 'Differs from original — likely contains stamp'
              : 'Signature detected; byte diff inconclusive.'}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <Button
            size="sm"
            className="bg-cyan-700 hover:bg-cyan-800 text-white"
            onClick={handleConfirm}
            disabled={confirming || signatureAbsent}
            data-testid="confirm-lock-button"
          >
            {confirming ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Confirming…</>
            ) : (
              'Confirm + Lock'
            )}
          </Button>
        </div>
      </div>
    );
  }

  // Idle state — Upload button (+ any error)
  return (
    <div
      className="flex flex-wrap items-center gap-2 px-4 py-2 border-b bg-background"
      data-testid="external-stamp-idle"
    >
      <Button
        variant="outline"
        size="sm"
        className="h-8 border-cyan-600 text-cyan-700 hover:bg-cyan-50"
        onClick={() => {
          setErrorMsg(null);
          fileInputRef.current?.click();
        }}
        data-testid="upload-stamped-pdf-button"
      >
        <Upload className="w-3.5 h-3.5 mr-1.5" />
        Upload Stamped PDF
      </Button>

      {errorMsg && (
        <div
          className="flex items-center gap-1.5 text-sm text-destructive"
          data-testid="upload-error-message"
        >
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="hidden"
        onChange={handleFileChange}
        data-testid="file-input"
      />
    </div>
  );
}
